<template>

  <createServiceInvoiceForm v-model:showCreateInvoice="showCreateInvoice" @created="openInvoiceView" />

  <viewServiceInvoice v-if="showViewServiceInvoice" v-model:showViewServiceInvoice="showViewServiceInvoice"
    :invoiceId="selectedInvoiceId" />

  <main
    class="flex flex-col w-full text-2xl font-semibold bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300"
    :style="{ fontFamily: 'Syne, sans-serif' }">

    <!-- Small Screen Navbar -->
    <nav
      class="sm:hidden w-full bg-[#E87055] dark:bg-white/10 backdrop-blur-md text-white flex justify-between items-center px-4 py-3 transition-colors duration-300">

      <div
        class="font-bold text-lg bg-white/20 dark:bg-white/50 backdrop-blur-md rounded-md px-3 shadow-lg transition-colors duration-300">
        <img class="w-60" src="../assets/logo-header.png" alt="Logo" />
      </div>

      <button @click="logout" class="text-white font-medium hover:text-gray-200">
        <logout class="w-8 h-8" />
      </button>

    </nav>

    <div class="max-w-[1600px] w-full mx-auto px-10 flex-1">

      <!-- Header -->
      <header class="h-20 flex items-center mt-3 gap-2">
        <h1 class="text-2xl font-bold text-brand_gray3 dark:text-gray-200 tracking-wide">
          Service Invoice
        </h1>
        <p class="text-base text-brand_orange2">
          ( {{ totalRecords }} Total Results )
        </p>
      </header>

      <!-- Divider -->
      <hr class="border-t-2 border-[#E87055] dark:border-[#ffa270] mb-6 w-full transition-colors duration-300" />

      <!-- Toolbar -->
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-2 sm:gap-0">

        <!-- Left: Search Filters -->
        <div class="flex flex-1 flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 max-w-xl w-full">

          <input @input="fetchPaginatedInvoice" v-model="filterSearch" type="text"
            placeholder="Search by Invoice Number..."
            class="h-10 px-3 bg-white dark:bg-gray-700 text-black dark:text-gray-200 rounded-sm border border-brand_gray2 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand_orange1 text-base font-normal placeholder:text-brand_gray2 dark:placeholder:text-gray-400 w-full sm:w-1/2 transition-colors duration-300" />

          <input @input="fetchPaginatedInvoice" v-model="filterPayee" type="text" placeholder="Search by Payee..."
            class="h-10 px-3 bg-white dark:bg-gray-700 text-black dark:text-gray-200 rounded-sm border border-brand_gray2 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand_orange1 text-base font-normal placeholder:text-brand_gray2 dark:placeholder:text-gray-400 w-full sm:w-1/2 transition-colors duration-300" />

        </div>

        <!-- Right -->
        <div class="flex items-center gap-2 w-full sm:w-auto">

          <!-- VIEWING + SUPER (REPORT) -->
          <div v-if="canGenerateReport" class="relative">

            <button @click="showReportPanel = !showReportPanel"
              class="h-10 flex items-center justify-center gap-2 px-4 rounded-md text-base font-medium tracking-wide transition-colors duration-200 bg-[#E87055] dark:bg-orange-600 hover:bg-brand_orange2 text-white">

              <AddSvgIcon class="w-5 h-5" />
              <span>Generate Report</span>
            </button>

            <!-- MODAL -->
            <div v-if="showReportPanel" class="fixed inset-0 z-50 flex items-center justify-center px-4">

              <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showReportPanel = false"></div>

              <div class="relative w-full max-w-sm bg-white dark:bg-gray-900 border-2 border-[#E87055]
      shadow-2xl rounded-md p-5 text-sm">

                <div class="mb-4">
                  <p class="text-sm font-bold text-[#E87055] uppercase tracking-wide">
                    Service Report Generator
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Select date range to export invoice data
                  </p>
                </div>

                <div class="flex flex-col gap-4">

                  <div>
                    <label class="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Start Date
                    </label>
                    <input v-model="reportFilters.startDate" type="date" class="h-10 w-full px-3 mt-1 bg-gray-50 dark:bg-gray-800
            border border-gray-300 dark:border-gray-700 rounded-md
            focus:outline-none focus:ring-2 focus:ring-[#E87055]" />
                  </div>

                  <div>
                    <label class="text-xs font-medium text-gray-600 dark:text-gray-300">
                      End Date
                    </label>
                    <input v-model="reportFilters.endDate" type="date" class="h-10 w-full px-3 mt-1 bg-gray-50 dark:bg-gray-800
            border border-gray-300 dark:border-gray-700 rounded-md
            focus:outline-none focus:ring-2 focus:ring-[#E87055]" />
                  </div>

                  <button @click="downloadReport" :disabled="isDownloadingReport" class="h-10 w-full bg-[#E87055] hover:bg-[#d75c3d] text-white font-semibold rounded-md
          disabled:opacity-60 disabled:cursor-not-allowed">

                    <span v-if="isDownloadingReport">Generating...</span>
                    <span v-else>Download Excel Report</span>

                  </button>

                </div>

              </div>
            </div>

          </div>


          <!-- ADMIN + SUPER (CREATE INVOICE) -->
          <div v-if="canCreateInvoice">
            <button @click="showCreateInvoice = true"
              class="h-10 flex items-center justify-center gap-2 px-4 rounded-md text-base font-medium tracking-wide transition-colors duration-200 bg-[#E87055] dark:bg-orange-600 hover:bg-brand_orange2 text-white">

              <AddSvgIcon class="w-5 h-5" />
              <span>Create Invoice</span>
            </button>
          </div>

        </div>
      </div>

      <!-- Table Section -->
      <div class="mt-4 w-full overflow-x-auto relative h-[70%]">

        <div v-if="isLoading"
          class="absolute inset-0 bg-white/70 dark:bg-gray-800/70 flex flex-col justify-center items-center z-50">
          <div class="w-10 h-10 border-4 border-brand_orange2 border-t-transparent rounded-full animate-spin"></div>
          <p class="mt-3 text-gray-700 dark:text-gray-200 font-medium text-sm">
            Loading invoices...
          </p>
        </div>

        <table
          class="table-fixed w-full border-collapse text-base font-normal text-gray-700 dark:text-gray-200 transition-colors duration-300">

          <thead class="bg-brand_orange1 dark:bg-gray-500 text-brand_gray3 dark:text-gray-200">
            <tr>
              <th class="px-4 py-3 text-left w-[120px] whitespace-nowrap">
                Invoice No.
              </th>
              <th class="px-4 py-3 text-left w-[200px] whitespace-nowrap">
                Payee
              </th>
              <th class="px-4 py-3 text-left w-[300px] break-words">
                Description
              </th>
              <th class="px-4 py-3 text-left w-[100px] whitespace-nowrap">
                Amount
              </th>
              <th class="px-4 py-3 text-left w-[120px] whitespace-nowrap">
                Invoice Date
              </th>
              <th class="px-4 py-3 text-left w-[120px] whitespace-nowrap">
                Date Encoded
              </th>
              <th class="px-4 py-3 text-left w-[120px] whitespace-nowrap">
                Encoded By
              </th>
              <th class="px-4 py-3 w-[120px]">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="invoice in allServiceInvoice" :key="invoice.id"
              class="h-[50px] odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300">
              <td class="border px-4 truncate whitespace-nowrap">
                {{ invoice.invoice_number }}
              </td>
              <td class="border px-4 truncate whitespace-nowrap">
                {{ invoice.payee.sold_to.toUpperCase() }}
              </td>
              <td class="border px-4 truncate whitespace-nowrap">
                {{ invoice.invoiceItems[0]?.description?.toUpperCase() }}
              </td>
              <td class="border px-4 truncate text-right">
                {{ formatPeso(invoice.total_amount_due) }}
              </td>
              <td class="border px-4 truncate whitespace-nowrap">
                {{ formatDate(invoice.invoice_date) }}
              </td>
              <td class="border px-4 truncate whitespace-nowrap">
                {{ formatDate(invoice.createdAt) }}
              </td>
              <td class="border px-4 truncate whitespace-nowrap">
                {{ invoice.user?.name }}
              </td>
              <td class="border px-4">
                <div class="flex items-center justify-center gap-2">
                  <button @click="openInvoiceView(invoice.id)" class="text-brand_orange2">
                    More Info
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

        </table>

      </div>

    </div>

    <!-- Paginator -->
    <div class="my-5 flex-shrink-0">
      <Paginator class="text-sm min-h-0 h-8" :first="first" :rows="rows" :totalRecords="totalRecords"
        @page="onPageChange" />
    </div>

    <!-- Footer -->
    <footer
      class="mt-auto px-10 py-3 text-sm text-gray-400 dark:text-gray-500 flex justify-center transition-colors duration-300">
      <version />
    </footer>

  </main>
</template>

<script>
import { inject, ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import createServiceInvoiceForm from "@/components/modals/createServiceInvoiceForm.vue";
import viewServiceInvoice from "@/components/modals/viewServiceInvoice.vue";
import version from "@/components/common/version.vue";

import AddSvgIcon from "@/components/svg/addSvgIcon.vue";
import logout from "@/components/svg/logoutSvgIcon.vue";

export default {
  components: {

    createServiceInvoiceForm,
    viewServiceInvoice,
    version,
    AddSvgIcon,
    logout,

  },

  setup() {
    const axios = inject("$axios");
    const router = useRouter();
    const store = useStore();

    const userData = computed(() => store.getters.getUserData);

    const first = ref(0);
    const rows = ref(10);
    const totalRecords = ref(0);
    const allServiceInvoice = ref([]);

    const filterSearch = ref("");
    const filterPayee = ref("");

    const isLoading = ref(false);

    const showCreateInvoice = ref(false);
    const showViewServiceInvoice = ref(false);
    const selectedInvoiceId = ref(null);

    // INLINE REPORT
    const showReportPanel = ref(false);

    const reportFilters = ref({
      startDate: "",
      endDate: "",
    });

    const isDownloadingReport = ref(false);

    const role = computed(() => userData.value?.role);

    const canGenerateReport = computed(() =>
      role.value === "viewing" || role.value === "super"
    );

    const canCreateInvoice = computed(() =>
      role.value === "admin" || role.value === "super" || role.value === "user"
    );

    const fetchPaginatedInvoice = async (page = 1, perPage = 10) => {
      isLoading.value = true;

      try {
        const token = localStorage.getItem("token");

        const res = await axios.get("/api/invoice/get/paginated", {
          params: {
            page,
            limit: perPage,
            invoiceNumber: filterSearch.value || undefined,
            payee: filterPayee.value || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        allServiceInvoice.value = res.data.data.rows || [];
        totalRecords.value = res.data.data.count || 0;

      } finally {
        isLoading.value = false;
      }
    };

    const downloadReport = async () => {

      if (!reportFilters.value.startDate || !reportFilters.value.endDate) {
        alert("Select dates first");
        return;
      }

      try {
        isDownloadingReport.value = true; // 🔥 START LOADING

        const startDate = reportFilters.value.startDate;
        const endDate = reportFilters.value.endDate;

        const res = await axios.get("/api/invoice/get/report", {
          params: {
            startDate,
            endDate,
          },
          responseType: "blob",
        });

        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const formatFileDate = (d) => {
          if (!d) return "all";
          return new Date(d).toISOString().split("T")[0];
        };

        const fileName = `service-invoice-report_${formatFileDate(startDate)}_to_${formatFileDate(endDate)}.xlsx`;

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        showReportPanel.value = false;

      } finally {
        isDownloadingReport.value = false; // 🔥 STOP LOADING (always runs)
      }
    };

    const onPageChange = (event) => {
      first.value = event.first;
      rows.value = event.rows;

      const page = event.page + 1; // PrimeVue is 0-based
      const perPage = event.rows;

      fetchPaginatedInvoice(page, perPage);
    };

    // Utils
    const formatPeso = (amount) => {
      if (amount == null) return "₱ 0.00";
      return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
      }).format(amount);
    };

    // Utils
    const formatDate = (isoString) => {
      const d = new Date(isoString);
      return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
        d.getDate()
      ).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
    };

    const openInvoiceView = (id) => {
      selectedInvoiceId.value = id;
      showCreateInvoice.value = false;
      showViewServiceInvoice.value = true;
    };

    const logout = () => {
      localStorage.clear();
      router.push("/");
    };

    onMounted(fetchPaginatedInvoice);

    return {
      userData,
      first,
      rows,
      totalRecords,
      allServiceInvoice,

      filterSearch,
      filterPayee,
      isLoading,

      showCreateInvoice,
      showViewServiceInvoice,
      selectedInvoiceId,

      showReportPanel,
      reportFilters,
      downloadReport,

      fetchPaginatedInvoice,
      openInvoiceView,
      logout,

      formatPeso,
      formatDate,
      onPageChange,
      isDownloadingReport,

      canGenerateReport,
      canCreateInvoice,
    };
  },
};
</script>